// @ts-nocheck
/**
 * Condenses Stamped's hydrated star-rating badge into the product-card
 * treatment: one star, the average, and the review count — "★ 4.7 (45)".
 *
 * The numbers come from Stamped's own markup (`data-rating` on .stamped-badge,
 * `data-reviews` on the caption), so a card always agrees with the product
 * page. The Shopify `reviews.*` metafields Stamped syncs are partial and cannot
 * be used for this — see the note in blocks/stempedraiting.liquid.
 *
 * Stamped re-runs loadBadges whenever cards are added (filtering, pagination,
 * quick add), replacing the span's contents with its own markup again, so the
 * rewrite is driven by a MutationObserver rather than a one-off pass.
 */
(() => {
  const WRAPPER = '.stamped-rating-badge-wrapper[data-stamped-condensed]';

  /** Matches the card's own abbreviation: 1.2k, 7k, 12k+. */
  const abbreviate = (count) => {
    if (count >= 10000) return `${Math.floor(count / 1000)}k+`;
    if (count >= 1000) {
      const tenths = Math.ceil(count / 100);
      const whole = Math.floor(tenths / 10);
      const decimal = tenths % 10;
      return decimal === 0 ? `${whole}k` : `${whole}.${decimal}k`;
    }
    return String(count);
  };

  const condense = (wrapper) => {
    const badge = wrapper.querySelector('.stamped-badge');
    if (!badge) return;

    const caption = badge.querySelector('.stamped-badge-caption');
    // Already rewritten — and this is also what stops our own DOM writes from
    // re-entering the observer indefinitely.
    if (!caption || caption.hasAttribute('data-condensed-ready')) return;

    const count = parseInt(caption.getAttribute('data-reviews') || caption.textContent, 10) || 0;
    const rating = badge.getAttribute('data-rating') || caption.getAttribute('data-rating') || '';

    if (!count || !rating) {
      // Stamped's zero-review badge is five outline stars, and the condensed
      // rules leave the first one standing, so it would sit next to the "No
      // reviews yet" label as a star with no rating behind it. The markup is
      // removed rather than hidden because the host cannot be hidden from a
      // stylesheet: it carries both .stamped-product-reviews-badge and
      // .stamped-main-badge, and collection-product-card.css forces
      // `display: flex !important` on the latter at a higher specificity.
      badge.remove();
      wrapper.setAttribute('data-stamped-state', 'empty');
      return;
    }

    // Stamped paints each star with an inline `color:#9a8758 !important`, which
    // outranks any stylesheet. Dropping it hands the color back to the card.
    badge.querySelectorAll('.stamped-fa').forEach((star) => star.style.removeProperty('color'));

    // Stamped's star row carries role="img" with the full "Rated 4.7 out of 5
    // stars 45 reviews" label, so the caption is decoration for screen readers.
    caption.setAttribute('aria-hidden', 'true');
    caption.innerHTML = `<span class="pc-rating__value">${rating}</span> <span class="pc-rating__count">(${abbreviate(count)})</span>`;
    caption.setAttribute('data-condensed-ready', '');
    wrapper.setAttribute('data-stamped-state', 'ready');
  };

  const condenseWithin = (root) => {
    if (root.matches?.(WRAPPER)) condense(root);
    root.querySelectorAll?.(WRAPPER).forEach(condense);
  };

  /**
   * Stamped answers for a product with no reviews with a real badge — five
   * outline stars and an empty caption — so condense() flags those the moment
   * they land. A product their index has never seen gets left out of the
   * response altogether and its host stays empty, which is indistinguishable
   * from a request still in flight.
   *
   * So the empty hosts are only judged once the widget has gone quiet: every
   * `stamped:badges:loaded` (fired after each badges response) restarts the
   * timer, and whatever is still empty when it finally runs has nothing coming.
   * A late badge still wins — condense() flips the row back to "ready".
   *
   * Guarded on StampedFn: if the widget is blocked or never loads, no row
   * claims "No reviews yet" on the strength of a request that never happened.
   */
  let settleTimer = null;

  const settle = () => {
    if (!window.StampedFn) return;

    for (const wrapper of document.querySelectorAll(WRAPPER)) {
      if (wrapper.getAttribute('data-stamped-state')) continue;
      if (!wrapper.querySelector('.stamped-badge')) wrapper.setAttribute('data-stamped-state', 'empty');
    }
  };

  const scheduleSettle = () => {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(settle, 1200);
  };

  const start = () => {
    condenseWithin(document);
    document.addEventListener('stamped:badges:loaded', scheduleSettle);
    scheduleSettle();

    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const target = mutation.target;
        const wrapper = target instanceof Element ? target.closest(WRAPPER) : null;

        if (wrapper) {
          condense(wrapper);
          continue;
        }

        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          condenseWithin(node);

          // Cards arriving from filtering or pagination have badges of their
          // own still to fetch, so the settle pass waits for them too.
          if (node.matches?.(WRAPPER) || node.querySelector?.(WRAPPER)) scheduleSettle();
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
